import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle,
  GraduationCap,
  Globe2,
  Handshake,
  TrendingUp,
  BookOpen,
  Rocket,
  Network,
  BadgeCheck,
} from "lucide-react";
import Logo from "@/components/Logo";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

function Header({ user, onLogout }) {
  return (
    <header className="glass-header sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-8 text-[13px] font-semibold text-foreground/80">
          <a href="#tracks" className="link-underline">
            Training Tracks
          </a>
          <a href="#value" className="link-underline">
            Why Steadfast
          </a>
          <a href="#about" className="link-underline">
            Our Experience
          </a>
        </nav>
        <div className="flex items-center gap-2">
          {user ? (
            <>
              <Link
                data-testid="nav-dashboard"
                to={
                  user.role === "admin" || user.role === "super_admin"
                    ? "/admin"
                    : "/dashboard"
                }
                className="h-9 px-4 inline-flex items-center text-[13px] font-semibold text-foreground hover:bg-accent"
              >
                Dashboard
              </Link>
              <button
                onClick={onLogout}
                data-testid="nav-logout"
                className="h-9 px-4 inline-flex items-center bg-primary text-primary-foreground text-[13px] font-semibold hover:bg-primary/90"
              >
                Sign out
              </button>
            </>
          ) : (
            <>
              <Link
                data-testid="nav-login"
                to="/login"
                className="h-9 px-4 inline-flex items-center text-[13px] font-semibold text-foreground hover:bg-accent"
              >
                Log in
              </Link>
              <Link
                data-testid="nav-register"
                to="/register"
                className="h-9 px-4 inline-flex items-center bg-primary text-primary-foreground text-[13px] font-semibold hover:bg-primary/90"
              >
                Request access <ArrowRight className="ml-2 h-3.5 w-3.5" />
              </Link>
            </>
          )}
        </div>
      </div>
    </header>
  );
}

function Hero({ modules }) {
  return (
    <section className="relative border-b border-border bg-white overflow-hidden">
      <div className="absolute inset-0 bg-grid opacity-60" aria-hidden="true" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="lg:col-span-8"
        >
          <div className="overline mb-6">
            Steadfast IT Consulting &nbsp;/&nbsp; Learning Hub
          </div>
          <h1 className="font-display font-black tracking-[-0.04em] leading-[0.95] text-5xl sm:text-6xl lg:text-[84px] text-foreground">
            One platform.
            <br />
            <span className="text-primary">Multiple tracks.</span>
            <br />
            Infinite outcomes.
          </h1>
          <p className="mt-8 text-[17px] lg:text-[19px] text-muted-foreground max-w-2xl leading-relaxed">
            Learn niche technologies with{" "}
            <strong className="text-foreground font-semibold">Steadfast</strong>{" "}
            — a global training partner serving leading universities, corporate
            L&D teams, and government upskilling programs. From cloud, data, and
            AI to SAP, Salesforce, and ServiceNow, every track is curated by
            practitioners who build these systems for a living.
          </p>
          <div className="mt-10 flex flex-wrap items-center gap-3">
            <Link
              data-testid="hero-register"
              to="/register"
              className="h-12 px-7 inline-flex items-center bg-primary text-primary-foreground font-semibold text-[14px] hover:bg-[#001d6b] transition-colors"
            >
              Request access <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
            <a
              href="#tracks"
              className="h-12 px-7 inline-flex items-center border border-border bg-white font-semibold text-[14px] hover:bg-accent"
            >
              Browse training tracks
            </a>
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-[13px] text-muted-foreground">
            <span className="inline-flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-primary" /> Curated by
              industry practitioners
            </span>
            <span className="inline-flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-primary" /> Verifiable
              completion certificates
            </span>
            <span className="inline-flex items-center gap-2">
              <CheckCircle className="h-4 w-4 text-primary" /> Trusted by
              universities &amp; corporates
            </span>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="lg:col-span-4 lg:pl-6"
        >
          <div className="border border-border bg-white p-6">
            <div className="overline mb-3">Live catalog</div>
            <div className="space-y-3">
              {modules.slice(0, 4).map((r) => (
                <div
                  key={r.slug}
                  className="flex items-start justify-between gap-3 pb-3 border-b border-border last:border-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <div className="font-display font-bold text-[14.5px] leading-tight truncate">
                      {r.name}
                    </div>
                    <div className="text-[11.5px] text-muted-foreground mt-1 font-mono">
                      {r.slug}
                    </div>
                  </div>
                  <span className="sharp-badge text-primary border-primary/30 shrink-0">
                    Live
                  </span>
                </div>
              ))}
            </div>
            <div className="mt-6 border-t border-border pt-5 grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="font-display font-black text-2xl">
                  {modules.length}
                </div>
                <div className="overline mt-1">Tracks</div>
              </div>
              <div>
                <div className="font-display font-black text-2xl">100+</div>
                <div className="overline mt-1">Partners</div>
              </div>
              <div>
                <div className="font-display font-black text-2xl text-primary">
                  15+
                </div>
                <div className="overline mt-1">Years</div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function TrackCard({ m, index, onLaunch, isAssigned, isRequested }) {
  return (
    <button
      type="button"
      onClick={() => onLaunch(m)}
      data-testid={`track-card-${m.slug}`}
      className="group text-left p-7 lg:p-8 card-lift border-r border-b border-border last:border-r-0 relative bg-white hover:bg-accent/30"
    >
      <div className="flex items-center justify-between mb-5">
        <span className="sharp-badge">
          Track {String(index + 1).padStart(2, "0")}
        </span>
        {isAssigned ? (
          <span className="sharp-badge text-emerald-700 border-emerald-500/40 bg-emerald-500/10">
            Assigned
          </span>
        ) : isRequested ? (
          <span className="sharp-badge text-amber-700 border-amber-500/40 bg-amber-500/10">
            Requested
          </span>
        ) : (
          <span className="text-[11px] text-muted-foreground font-semibold uppercase tracking-widest">
            Live
          </span>
        )}
      </div>
      <h3 className="font-display font-black text-xl lg:text-2xl leading-tight tracking-tight mb-3">
        {m.name}
      </h3>
      <p className="text-muted-foreground text-[13.5px] leading-relaxed line-clamp-3">
        {m.description}
      </p>
      <div className="mt-6 inline-flex items-center gap-2 text-primary font-semibold text-[13px] transition-all group-hover:gap-3">
        {isAssigned
          ? "Launch module"
          : isRequested
            ? "Awaiting approval"
            : "Request this track"}{" "}
        <ArrowRight className="h-4 w-4" />
      </div>
    </button>
  );
}

function Tracks({ modules, user, onLaunch }) {
  return (
    <section id="tracks" className="border-b border-border bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end mb-12">
          <div className="lg:col-span-7">
            <div className="overline mb-4">
              01 &nbsp;/&nbsp; Training tracks
            </div>
            <h2 className="font-display font-black text-3xl lg:text-5xl leading-tight text-foreground tracking-tight">
              Curated programs. Industry-grade depth.
            </h2>
          </div>
          <p className="lg:col-span-5 text-muted-foreground text-[15px] leading-relaxed">
            Pick a track below to submit a request. An admin reviews each
            application and unlocks the curriculum for approved learners.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-border">
          {modules.map((m, i) => (
            <TrackCard
              key={m.slug}
              m={m}
              index={i}
              onLaunch={onLaunch}
              isAssigned={user?.assigned_modules?.includes(m.slug)}
              isRequested={user?.requested_modules?.includes(m.slug)}
            />
          ))}
          {/* Ghost cell to keep last row consistent */}
          {modules.length % 3 !== 0 && (
            <div className="hidden lg:block border-r border-b border-border bg-accent/20" />
          )}
        </div>
      </div>
    </section>
  );
}

function ValueSection() {
  const items = [
    {
      icon: Rocket,
      title: "Ship-ready, not slide-ready",
      body: "Niche technologies like Cloud, AI/ML, SAP, Salesforce, and ServiceNow power the world's largest enterprises. Our curricula don't teach theory — they teach the exact workflows, configurations, and decisions hiring managers test for.",
    },
    {
      icon: TrendingUp,
      title: "A measurable employability lift",
      body: "Learners who complete a niche-tech track consistently report stronger interview outcomes, faster onboarding at their first role, and sharper lateral movement inside existing organizations.",
    },
    {
      icon: BadgeCheck,
      title: "Credentials worth showing",
      body: "Every completion issues a verifiable certificate from Steadfast IT Consulting — shareable on LinkedIn, attachable to a resume, and independently verifiable via a public link.",
    },
  ];
  return (
    <section id="value" className="border-b border-border bg-accent/40">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="overline mb-4">
          02 &nbsp;/&nbsp; Why niche technology training
        </div>
        <h2 className="font-display font-black text-3xl lg:text-5xl leading-tight tracking-tight max-w-3xl mb-14">
          A specialised skillset is the shortest distance between a resume and
          an offer letter.
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 border border-border bg-white">
          {items.map((it, i) => (
            <div
              key={it.title}
              className={`p-7 lg:p-9 ${i < items.length - 1 ? "md:border-r border-border" : ""} ${i < items.length - 1 && i === 0 ? "border-b md:border-b-0" : ""}`}
            >
              <it.icon className="h-6 w-6 text-primary" aria-hidden="true" />
              <h3 className="font-display font-bold text-[19px] lg:text-[21px] mt-6 mb-3 leading-tight tracking-tight">
                {it.title}
              </h3>
              <p className="text-[14px] text-muted-foreground leading-relaxed">
                {it.body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ExperienceSection() {
  return (
    <section id="about" className="bg-secondary text-secondary-foreground">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div
              className="overline mb-4"
              style={{ color: "rgba(255,255,255,0.55)" }}
            >
              03 &nbsp;/&nbsp; Steadfast IT Consulting
            </div>
            <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tight leading-tight text-white mb-8">
              A training partner the world's software companies trust with their
              people.
            </h2>
            <div className="space-y-5 text-white/70 text-[15.5px] leading-relaxed">
              <p>
                For over a decade, Steadfast IT Consulting has operated at the
                intersection of enterprise software and education — delivering
                structured learning programs to universities, corporate L&amp;D
                divisions, government upskilling initiatives, and
                direct-to-learner audiences across multiple geographies.
              </p>
              <p>
                Our global delivery model combines live-instructor cohorts,
                on-demand curricula, and certification pathways that have been
                co-developed with leading OEMs and software vendors. We're a
                trusted partner for the platforms we teach — which is why our
                content stays aligned to what these vendors actually deploy in
                the field.
              </p>
              <p>
                From campus hiring pipelines to enterprise rollouts, we operate
                as a long-term capability-building partner rather than a one-off
                training vendor.
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="grid grid-cols-2 gap-0 border border-white/15">
              {[
                { icon: Globe2, label: "Global delivery", val: "Multi-region" },
                {
                  icon: GraduationCap,
                  label: "Universities served",
                  val: "Leading institutions",
                },
                {
                  icon: Handshake,
                  label: "OEM partnerships",
                  val: "Trusted training partner",
                },
                {
                  icon: Network,
                  label: "Corporate L&D",
                  val: "Enterprise programs",
                },
              ].map((m, i) => (
                <div
                  key={m.label}
                  className={`p-6 ${i % 2 === 0 ? "border-r" : ""} ${i < 2 ? "border-b" : ""} border-white/15`}
                >
                  <m.icon className="h-5 w-5 text-white/70" />
                  <div
                    className="overline mt-5"
                    style={{ color: "rgba(255,255,255,0.5)" }}
                  >
                    {m.label}
                  </div>
                  <div className="font-display font-bold text-[15px] mt-2 text-white leading-snug">
                    {m.val}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-8 border border-white/15 p-6">
              <div
                className="overline mb-3"
                style={{ color: "rgba(255,255,255,0.5)" }}
              >
                Partnerships across
              </div>
              <div className="font-display font-black text-white text-[22px] lg:text-[26px] leading-[1.15] tracking-tight">
                Cloud &middot; AI/ML &middot; Data Science &middot; SAP &middot;
                Salesforce &middot; ServiceNow &middot; and public-sector
                upskilling programs.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function CTA() {
  return (
    <section className="border-t border-border bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
        <div className="lg:col-span-8">
          <div className="overline mb-4">Start learning</div>
          <h2 className="font-display font-black text-3xl lg:text-5xl tracking-tight leading-tight max-w-2xl">
            Pick a track above, or create an account to request one.
          </h2>
        </div>
        <div className="lg:col-span-4 flex flex-wrap gap-2 lg:justify-end">
          <Link
            to="/register"
            data-testid="cta-register"
            className="h-12 px-7 inline-flex items-center bg-primary text-primary-foreground font-semibold text-[13.5px] hover:bg-[#001d6b]"
          >
            Request access <ArrowRight className="ml-2 h-4 w-4" />
          </Link>
          <Link
            to="/login"
            className="h-12 px-7 inline-flex items-center border border-border font-semibold text-[13.5px] hover:bg-accent"
          >
            I already have an account
          </Link>
        </div>
      </div>
    </section>
  );
}

function Footer() {
  return (
    <footer className="bg-[#090E17] text-white/80">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 border-b border-white/10 pb-10">
          <div>
            <Logo variant="dark" />
            <p className="mt-4 max-w-md text-[14px] text-white/60 leading-relaxed">
              Steadfast IT Consulting's unified training platform — niche
              technology curricula delivered at enterprise scale.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/register"
              className="h-11 px-6 inline-flex items-center bg-white text-[#090E17] font-semibold text-[13px] hover:bg-white/90"
            >
              Request access
            </Link>
            <Link
              to="/login"
              className="h-11 px-6 inline-flex items-center border border-white/20 text-white font-semibold text-[13px] hover:bg-white/10"
            >
              Log in
            </Link>
          </div>
        </div>
        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between text-[12px] text-white/50 gap-3">
          <div>
            © {new Date().getFullYear()} Steadfast IT Consulting. All rights
            reserved.
          </div>
          <div>Unified LMS &middot; Trusted training partner</div>
        </div>
      </div>
    </footer>
  );
}

function Toast({ msg }) {
  if (!msg) return null;
  return (
    <div
      data-testid="landing-toast"
      className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-[#090E17] text-white px-5 py-3 border border-white/10 text-[13.5px] font-semibold max-w-md text-center shadow-lg"
    >
      {msg}
    </div>
  );
}

export default function LandingPage() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const [modules, setModules] = useState([
    {
      slug: "cloud-computing",
      name: "Cloud Computing Fundamentals",
      description:
        "Build a strong foundation in cloud infrastructure, deployment models, and core service primitives used across AWS, Azure, and GCP.",
      external_url: "https://cloud-computing-scrom.vercel.app",
      image_url:
        "https://images.unsplash.com/photo-1667984390553-7f439e6ae401?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
      total_topics: null,
      total_sections: null,
      is_active: true,
    },
    {
      slug: "data-science",
      name: "Data Science & Analytics",
      description:
        "Hands-on curriculum spanning statistics, data wrangling, SQL, Python, and modern analytics techniques used by enterprise data teams.",
      external_url: "https://analytics-datascience-scorm.vercel.app",
      image_url:
        "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
      total_topics: null,
      total_sections: null,
      is_active: true,
    },
    {
      slug: "ai-ml",
      name: "Artificial Intelligence & Machine Learning",
      description:
        "From classical ML to modern deep learning and LLM workflows — a practical track for engineers entering the AI economy.",
      external_url: "https://aiml-elearning.vercel.app",
      image_url:
        "https://images.unsplash.com/photo-1677442135703-1787eea5ce01?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
      total_topics: null,
      total_sections: null,
      is_active: true,
    },
    {
      slug: "salesforce",
      name: "Salesforce Fundamentals",
      description:
        "A comprehensive Salesforce Administration program covering 21 sections and 82 topics with interactive quizzes and assessments.",
      external_url: "https://salesforce-elearning-scorm.vercel.app",
      image_url:
        "https://images.unsplash.com/photo-1667984390553-7f439e6ae401?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
      total_topics: 82,
      total_sections: 21,
      is_active: true,
    },
    {
      slug: "servicenow",
      name: "ServiceNow Fundamentals",
      description:
        "Master the ServiceNow platform from navigation to advanced administration. Complete all modules to earn your certificate of completion.",
      external_url: "https://servicenow-elearning.vercel.app",
      image_url:
        "https://servicenow-learn-pro.preview.emergentagent.com/servicenow-logo.png",
      total_topics: 40,
      total_sections: 12,
      is_active: true,
    },
    {
      slug: "sap",
      name: "SAP Fundamentals",
      description:
        "Core SAP modules, navigation, and enterprise workflows — designed for consultants joining SAP ecosystem projects across industries.",
      external_url: "https://sap-scorm.vercel.app",
      image_url:
        "https://images.unsplash.com/photo-1556742044-3c52d6e88c62?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
      total_topics: null,
      total_sections: null,
      is_active: true,
    },
    {
      slug: "ai-mh-govt",
      name: "AI Essentials of MH Govt",
      description:
        "A purpose-built AI literacy program aligned to the Maharashtra Government's upskilling initiative — core concepts, applied use-cases, and ethics.",
      external_url: "https://mh-gov-aiml-scrom.vercel.app",
      image_url:
        "https://images.unsplash.com/photo-1518770660439-4636190af475?crop=entropy&cs=srgb&fm=jpg&q=85&w=900",
      total_topics: null,
      total_sections: null,
      is_active: true,
    },
  ]);
  const [toast, setToast] = useState("");

  useEffect(() => {
    api
      .get("/modules")
      .then((r) => setModules(r.data))
      .catch(() => {});
  }, []);

  const showToast = (m) => {
    setToast(m);
    setTimeout(() => setToast(""), 4000);
  };

  const handleLaunch = async (m) => {
    if (!user) {
      nav(`/register?module=${m.slug}`);
      return;
    }
    // Logged in
    if (user.status === "pending") {
      nav("/awaiting-approval");
      return;
    }
    if (user.assigned_modules?.includes(m.slug)) {
      nav(`/course/${m.slug}`);
      return;
    }
    // Request additional module
    try {
      const { data } = await api.post("/me/request-module", { slug: m.slug });
      await refresh();
      if (data.status === "requested")
        showToast(
          `Request submitted for ${m.name}. An admin will review it shortly.`,
        );
      else if (data.status === "already_requested")
        showToast(
          `You've already requested ${m.name}. Awaiting admin approval.`,
        );
    } catch (e) {
      showToast(errorMessage(e));
    }
  };

  return (
    <div className="App min-h-screen bg-background">
      <Header user={user} onLogout={logout} />
      <Hero modules={modules} />
      <Tracks modules={modules} user={user} onLaunch={handleLaunch} />
      <ValueSection />
      <ExperienceSection />
      <CTA />
      <Footer />
      <Toast msg={toast} />
    </div>
  );
}
